import { Component, inject, signal, effect, computed } from '@angular/core';
import { LayoutShellComponent, UserInfo } from '@olonera/layout';
import { NavigationConfigService } from '@olonera/layout';
import { AuthService, SuspensionReason } from '@olonera/auth';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { PractitionerService } from '@olonera/api';
import { PageTitleService } from '@olonera/shared';

@Component({
  selector: 'app-authenticated-layout',
  standalone: true,
  imports: [LayoutShellComponent, CommonModule],
  template: `
    <lib-layout-shell
      [navSections]="navSections"
      [pageTitle]="pageTitleService.pageTitle()"
      [pageSubtitle]="pageTitleService.pageSubtitle()"
      [user]="user()"
      userSubtitle="Medical Practitioner"
      role="Practitioner"
      (logout)="onLogout()"
      (settingsClick)="onSettingsClick()"
      (profileClick)="onProfileClick()"
      (notificationClick)="onNotificationClick()"
    >
      <div class="main-content-wrapper">
        <ng-content></ng-content>
        
        <!-- Verification Overlay -->
        <div class="verification-overlay" *ngIf="showVerificationOverlay()">
          <div class="overlay-card">
            <div class="icon-circle" [class]="getStatusClass()">
              <svg *ngIf="authService.verificationStatus() === 4 || authService.currentUser()?.isLicenseExpired || authService.verificationStatus() === 2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
              <svg *ngIf="authService.verificationStatus() === 3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <svg *ngIf="authService.verificationStatus() === 0 || authService.verificationStatus() === undefined" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
            </div>
            
            <h2>{{ getOverlayTitle() }}</h2>
            <p class="description">{{ getOverlayDescription() }}</p>

            <!-- Suspended: Reason block (always shown) + Admin Note (only if notes present) -->
            @if (authService.verificationStatus() === 4) {
              <div [class]="'reason-box ' + getStatusClass()">
                <strong>Reason:</strong>
                <p>{{ formatSuspensionReason(authService.currentUser()?.suspensionReason) }}</p>
              </div>
              @if (authService.currentUser()?.suspensionNotes) {
                <div [class]="'reason-box ' + getStatusClass()">
                  <strong>Admin Note:</strong>
                  <p>{{ authService.currentUser()?.suspensionNotes }}</p>
                </div>
              }
            }

            <!-- All other non-verified statuses: single Admin Note block -->
            @if (authService.verificationStatus() !== 4 && !authService.currentUser()?.isLicenseExpired && authService.verificationReason()) {
              <div [class]="'reason-box ' + getStatusClass()">
                <strong>Admin Note:</strong>
                <p>{{ authService.verificationReason() }}</p>
              </div>
            }

            <div class="actions">
              <button (click)="onSettingsClick()" class="btn-primary">
                Update Profile & Credentials
              </button>
            </div>
          </div>
        </div>
      </div>
    </lib-layout-shell>
  `,
  styles: [`
    :host {
      display: block;
      height: 100vh;
    }

    .main-content-wrapper {
      position: relative;
      height: 100%;
    }

    .verification-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.8);
      backdrop-filter: blur(4px);
      z-index: 100;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 2rem;
    }

    .overlay-card {
      background: white;
      padding: 3rem;
      border-radius: 1.5rem;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      max-width: 500px;
      width: 100%;
      text-align: center;
      border: 1px solid #f3f4f6;
    }

    .icon-circle {
      width: 4rem;
      height: 4rem;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1.5rem;
      
      svg { width: 2rem; height: 2rem; }
      
      &.pending { background: #f0fdfa; color: #0d9488; }
      &.verified { background: #ecfdf5; color: #059669; }
      &.rejected, &.suspended, &.expired { background: #fef2f2; color: #dc2626; }
      &.requestmoreinfo { background: #fff7ed; color: #ea580c; }
    }

    h2 {
      font-size: 1.5rem;
      font-weight: 700;
      color: #111827;
      margin-bottom: 0.75rem;
    }

    .description {
      color: #4b5563;
      margin-bottom: 2rem;
      line-height: 1.5;
    }

    .reason-box {
      background: #f9fafb;
      padding: 1rem;
      border-radius: 0.75rem;
      text-align: left;
      margin-bottom: 1rem;
      border-left: 4px solid #e5e7eb;

      strong { font-size: 0.75rem; color: #6b7280; text-transform: uppercase; }
      p { margin-top: 0.25rem; color: #1f2937; font-size: 0.875rem; }

      &.pending { border-left-color: #0d9488; }
      &.requestmoreinfo { border-left-color: #ea580c; }
      &.rejected, &.suspended, &.expired { border-left-color: #dc2626; }
    }

    .btn-primary {
      width: 100%;
      background: #222222;
      color: white;
      padding: 0.875rem;
      border-radius: 0.75rem;
      font-weight: 600;
      border: none;
      cursor: pointer;
      transition: all 0.2s;
      margin-top: 1rem;

      &:hover { background: #000; transform: translateY(-1px); }
    }
  `]
})
export class AuthenticatedLayoutComponent {
  public readonly authService = inject(AuthService);
  private readonly navConfig = inject(NavigationConfigService);
  private readonly router = inject(Router);
  private readonly practitionerService = inject(PractitionerService);
  protected readonly pageTitleService = inject(PageTitleService);
  private readonly navigationEnd = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects)
    )
  );

  user = signal<UserInfo | null>(null);
  navSections = this.navConfig.getPractitionerNav();
  private lastSyncedUserId: string | null = null;

  constructor() {
    effect(() => {
      const userDto = this.authService.currentUser();
      const route = this.navigationEnd(); // Trigger sync on every route change

      if (userDto) {
        if (userDto.id !== this.lastSyncedUserId) {
          this.lastSyncedUserId = userDto.id;
        }

        this.practitionerService.getMyProfile().subscribe({
          next: (profile: any) => {
            const now = new Date();
            const todayStr = now.toISOString().split('T')[0];
            const isLicenseExpired = profile.credentials?.some((c: any) => {
              if (c.isArchived || !c.expirationDate) return false;
              return c.expirationDate.split('T')[0] < todayStr;
            }) ?? false;

            // IMPORTANT: do NOT recalculate isVerified here — it depends on BAA grace
            // period state that only the backend (/me) knows about. We only sync the
            // fields that are genuinely owned by the profile endpoint.
            // Preserve requiresBaaReSign and isDocumentSigned from the current signal
            // so the onboarding guard keeps working correctly during grace period.
            const current = this.authService.currentUser();
            const hasChanges =
              userDto.verificationStatus !== profile.verificationStatus ||
              userDto.verificationNotes !== profile.verificationNotes ||
              userDto.suspensionReason !== profile.suspensionReason ||
              userDto.suspensionNotes !== profile.suspensionNotes ||
              userDto.isLicenseExpired !== isLicenseExpired;

            if (hasChanges) {
              this.authService.updateUserVerificationStatus({
                verificationStatus: profile.verificationStatus,
                verificationNotes: profile.verificationNotes,
                suspensionReason: profile.suspensionReason,
                suspensionNotes: profile.suspensionNotes,
                isLicenseExpired,
                // Explicitly preserve BAA-related fields from the current signal
                isVerified: current?.isVerified,
                isDocumentSigned: current?.isDocumentSigned,
                requiresBaaReSign: current?.requiresBaaReSign,
                isBaaInGracePeriod: current?.isBaaInGracePeriod,
              });
            }

            this.user.set({
              firstName: userDto.firstName,
              lastName: userDto.lastName,
              email: userDto.email,
              avatarUrl: profile.profilePhotoUrl || undefined
            });
          },
          error: () => {
            // If profile load fails, still show basic user info
            this.user.set({
              firstName: userDto.firstName,
              lastName: userDto.lastName,
              email: userDto.email
            });
          }
        });
      }
    });
  }

  onLogout(): void {
    this.authService.logout();
  }

  onSettingsClick(): void {
    this.router.navigate(['/settings/profile']);
  }

  onProfileClick(): void {
    this.router.navigate(['/settings/profile']);
  }

  onNotificationClick(): void {
    this.router.navigate(['/settings/profile']);
  }

  getStatusClass(): string {
    if (this.authService.currentUser()?.isLicenseExpired) {
      return 'expired';
    }
    const status = this.authService.verificationStatus();
    switch (status) {
      case 1: return 'verified';
      case 2: return 'rejected';
      case 3: return 'requestmoreinfo';
      case 4: return 'suspended';
      default: return 'pending';
    }
  }

  getStatusIcon(): string {
    const status = this.authService.verificationStatus();
    switch (status) {
      case 1: return 'check_circle';
      case 2: return 'cancel';
      case 4: return 'block';
      default: return 'info';
    }
  }

  getStatusLabel(): string {
    const status = this.authService.verificationStatus();
    switch (status) {
      case 1: return 'Verified';
      case 2: return 'Rejected';
      case 3: return 'Info Required';
      case 4: return 'Suspended';
      default: return 'Pending';
    }
  }

  showVerificationOverlay = computed(() => {
    const isVerified = this.authService.isVerified();
    const url = this.navigationEnd() || this.router.url;
    
    // Block if not verified AND on a clinical route
    // Allow profile settings so they can fix the issue
    return !isVerified && !url.includes('/settings/');
  });

  getOverlayTitle(): string {
    const status = this.authService.verificationStatus();
    const isExpired = this.authService.currentUser()?.isLicenseExpired;

    if (isExpired) return 'License Expired';
    
    switch (status) {
      case 4: return 'Account Suspended';
      case 3: return 'Action Required';
      case 2: return 'Verification Rejected';
      default: return 'Verification Pending';
    }
  }

  getOverlayDescription(): string {
    const status = this.authService.verificationStatus();
    const isExpired = this.authService.currentUser()?.isLicenseExpired;

    if (isExpired) return 'Your medical license has expired. Please upload a renewed license to regain access to clinical features.';

    switch (status) {
      case 4: return 'Your account has been suspended by the administration. Please review the details below.';
      case 3: return 'Action Required: Please update your profile as requested.';
      case 2: return 'Your professional verification was not successful. Please contact support or update your credentials.';
      default: return 'Your professional credentials are currently being reviewed by our administration team. This process usually takes 24-48 hours.';
    }
  }

  private static readonly suspensionReasonLabels: Record<string, string> = {
    LicenceExpired: 'Licence Expired',
    BackgroundCheckIssue: 'Background Check Issue',
    IncompleteVerification: 'Incomplete Verification',
    ScopeViolation: 'Scope Violation',
    TitleViolation: 'Title Violation',
    ClaimViolation: 'Claim Violation',
    ClientComplaint: 'Client Complaint',
    AdminDiscretion: 'Administrative Decision'
  };

  formatSuspensionReason(reason: SuspensionReason | string | undefined): string {
    if (!reason) return 'Administrative Decision';
    return AuthenticatedLayoutComponent.suspensionReasonLabels[reason] ?? String(reason);
  }
}
